import {User} from '../../core/model/user';
import * as auth from './auth.actions';
import {AuthActionTypes} from './auth.actions';
import {App, Preferences} from '../../core/model/user-preferences.model';
import {Application} from '../admin/application-settings/models/application.model';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {FeatureToggleModel} from '@app/views/common/feature-toggles/featue-toggle.model';
import {PIIShieldLevelConfig} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {PassportLoginIDP} from '@app/views/sso/identity-providers/idp-settings/passport-login-idp/passport-login-idp.model';


/**
 * The state.
 * @interface State
 */
export interface State {

    // boolean if user is authenticated
    authenticated: boolean;

    // error message
    error?: string;

    // true if we have attempted existing auth session
    loaded: boolean;

    // true when loading
    loading: boolean;

    // true if the content is visible
    visibleContent: boolean;

    // true if the navigation menu is opened
    menuOpened: boolean;

    // the authenticated user
    user?: User;

    // preferences of current user
    userPreferences?: Preferences;

    // current user (vendor) applications
    userApplications?: Application[];

    // redirect url after sign out
    signOutRedirectUrl: string;

    // userOrganization
    userOrganization?: DirectoryOrganization;

    featureToggles: FeatureToggleModel[];

    piiShieldLevelsConfig: PIIShieldLevelConfig[];

    passportLoginIDPs: PassportLoginIDP[];

    onboardingWizardCompletedSteps: string[];
}

/**
 * The initial state.
 */
export const initialState: State = {
    authenticated: null,
    loaded: false,
    loading: false,
    visibleContent: false,
    menuOpened: false,
    signOutRedirectUrl: null,
    featureToggles: [],
    piiShieldLevelsConfig: null,
    passportLoginIDPs: null,
    onboardingWizardCompletedSteps: null
};

/**
 * The initial objects to assign for objects loaded from localStorage.
 */
export const localStorageInitialObjects = {
    user: new User(),
    userPreferences: new Preferences()
};

export function authReducer(state: any = initialState, action: auth.Actions): State {

    switch (action. type) {
        case AuthActionTypes.AUTHENTICATE:
            return Object.assign({}, state, {
                error: undefined,
                loading: true
            });

        case AuthActionTypes.AUTHENTICATE_ERROR:
            return Object.assign({}, state, {
                authenticated: false,
                error: action.payload.error.message,
                loading: false,
                user: null
            });

        case AuthActionTypes.AUTHENTICATE_MFA_FIRST_SUCCESS:
            return Object.assign({}, state, {
                error: undefined,
                loading: false,
                user: action.payload
            });

        case AuthActionTypes.AUTHENTICATE_SETUP_MFA_FIRST_SUCCESS:
            return Object.assign({}, state, {
                error: undefined,
                loading: false,
                user: action.payload
            });

        case AuthActionTypes.AUTHENTICATE_SUCCESS:
            return Object.assign({}, state, {
                authenticated: true,
                error: undefined,
                loading: false,
                signOutRedirectUrl: undefined
            });

        case AuthActionTypes.AUTHENTICATED_SUCCESS:
            const user: User = action.payload.user;

            // verify user is not null
            if (user === null) {
                return state;
            }

            return Object.assign({}, state, {
                error: undefined,
                loaded: true,
                user: user
            });

        case AuthActionTypes.AUTHENTICATED_USER_REFRESH_SUCCESS:
            if (state.user?.etxOrgId) {
                action.payload.user.etxOrgId = state.user.etxOrgId;
            }
            return Object.assign({}, state, {
                user: action.payload.user
            });

        case AuthActionTypes.AUTHENTICATED_EXTRA_SUCCESS:
            const extra: any = action.payload.extra;

            if (extra === null) {
                return state;
            }

            return Object.assign({}, state, extra);

        case AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES_SUCCESS:
            const userPreferences = Object.assign(new Preferences(), state.userPreferences, action.payload.userPreferences);

            return Object.assign({}, state, {
                userPreferences: userPreferences
            });

        case AuthActionTypes.SIGN_OUT_SUCCESS:
        case AuthActionTypes.SIGN_OUT_ERROR:
            return Object.assign({}, state, {
                authenticated: false,
                error: undefined,
                user: undefined,
                userPreferences: undefined,
                userApplications: undefined,
                visibleContent: false,
                signOutRedirectUrl: (action.payload && action.payload.signOutRedirectUrl) ? action.payload.signOutRedirectUrl : '',
                userOrganization: undefined,
                piiShieldLevelsConfig: undefined,
                passportLoginIDPs: undefined,
            });

        case AuthActionTypes.SHOW_CONTENT:
            return Object.assign({}, state, {
                visibleContent: true
            });

        case AuthActionTypes.HIDE_CONTENT:
            return Object.assign({}, state, {
                visibleContent: false
            });

        case AuthActionTypes.SHOW_MENU:
            return Object.assign({}, state, {
                menuOpened: true
            });

        case AuthActionTypes.HIDE_MENU:
            return Object.assign({}, state, {
                menuOpened: false
            });

        case AuthActionTypes.SET_USER_ORGANIZATION:
            return Object.assign({}, state, {
                userOrganization: action.payload
            });

        case AuthActionTypes.SET_DISTRICT_FEATURE_TOGGLES_SUCCESS:
            return Object.assign({}, state, {
                featureToggles: action.payload
            });

        case AuthActionTypes.GET_DISTRICT_FEATURE_TOGGLES_FAILED:
            return Object.assign({}, state, {
                featureToggles: []
            });

        case AuthActionTypes.SET_VENDOR_FEATURE_TOGGLES_SUCCESS:
            return Object.assign({}, state, {
                featureToggles: action.payload
            });

        case AuthActionTypes.GET_VENDOR_FEATURE_TOGGLES_FAILED:
            return Object.assign({}, state, {
                featureToggles: []
            });

        case AuthActionTypes.SET_PII_SHIELD_LEVELS_CONFIG:
            return Object.assign({}, state, {
                piiShieldLevelsConfig: action.payload
            });

        case AuthActionTypes.SET_PASSPORT_LOGIN_IDP_LIST:
            return Object.assign({}, state, {
                passportLoginIDPs: action.payload
            });

        case AuthActionTypes.SET_ONBOARDING_WIZARD_COMPLETED_STEPS:
            return Object.assign({}, state, {
                onboardingWizardCompletedSteps: action.payload
            });

        default:
            return state;

    }
}

/**
 * Returns true if the user is authenticated.
 * @function isAuthenticated
 * @param state
 * @returns
 */
export const isAuthenticated = (state: State) => state.authenticated;

/**
 * Returns the authentication error.
 * @function getAuthenticationError
 * @param state
 * @returns
 */
export const getAuthenticationError = (state: State) => state.error;

/**
 * Returns true if request is in progress.
 * @function isLoading
 * @param state
 * @returns
 */
export const isLoading = (state: State) => state.loading;

/**
 * Returns authenticated user.
 * @function getAuthenticatedUser
 * @param state
 * @returns
 */
export const getAuthenticatedUser = (state: State) => state.user;

/**
 * Returns authenticated user preferences.
 * @function getAuthenticatedUserPreferences
 * @param state
 * @returns
 */
export const getAuthenticatedUserPreferences = (state: State) => state.userPreferences;

/**
 * Returns authenticated user applications.
 * @function getAuthenticatedUserApplications
 * @param state
 * @returns
 */
export const getAuthenticatedUserApplications = (state: State) => state.userApplications;

/**
 * Returns signOutRedirectUrl.
 * @function getSignOutRedirectUrl
 * @param state
 * @returns
 */
export const getSignOutRedirectUrl = (state: State) => state.signOutRedirectUrl;

/**
 * Returns userOrganization.
 * @function getUserOrganization
 * @param state
 * @returns
 */
export const getUserOrganization = (state: State) => state.userOrganization;

/**
 * Returns true if the content is visible.
 * @function isVisibleContent
 * @param state
 * @returns
 */
export const isVisibleContent = (state: State) => state.visibleContent;

/**
 * Returns true if the navigation menu is opened.
 * @function isMenuOpened
 * @param state
 * @returns
 */
export const isMenuOpened = (state: State) => state.menuOpened;

/**
 * Returns org featureToggles.
 * @function featureToggles
 * @param state
 * @returns
 */
export const featureToggles = (state: State) => state.featureToggles;

/**
 * Returns System PII levels config.
 * @function getPIIShieldLevelsConfig
 * @param {State} state
 * @returns
 */
export const getPIIShieldLevelsConfig = (state: State) => state.piiShieldLevelsConfig;

/**
 * Returns Passport Login IDP list.
 * @function getPassportLoginIDPs
 * @param state
 * @returns {PassportLoginIDP[]}
 */
export const getPassportLoginIDPs = (state: State) => state.passportLoginIDPs;

/**
 * Returns Onboarding Wizard Completed Steps.
 * @function getOnboardingWizardCompletedSteps
 * @param state
 * @returns {OnboardingWizardStep[]}
 */
export const getOnboardingWizardCompletedSteps = (state: State) => state.onboardingWizardCompletedSteps;
